import { fieldAnswered, fieldScore, sectionScores, type Band, type RiskConfig } from '@ehr/shared';
import { buildBlocks, type LibField } from '../components/FormModal.js';

/**
 * credifyfbs#129 (Chase, 10-07): a scored form's History popup in the client drawer shows
 * the client's scores fill by fill, as the Form Builder's Score history does: one row per
 * scored item, one column per fill (oldest first), then the total, the severity band and
 * any alert. Scored items are the scale matrices the form view already scores (a run of
 * 2+ questions sharing a scale of 3+ options; an answer scores its option's position).
 * Pure helpers, no DOM.
 */
export interface ScoredItem {
  id: string;
  label: string;
  options: string[];
}
export type Severity = 'low' | 'mild' | 'mod' | 'high';
export interface ScoreBand {
  min: number;
  max: number;
  label: string;
  severity: Severity;
}
export interface Instrument {
  name: string;
  items: number;
  bands: ScoreBand[];
  /** An item whose score above 0 raises an alert (PHQ-9 item 9). */
  alert?: { itemIndex: number; msg: string };
}

// Same bands as the Form Builder's System Forms (INSTRUMENTS in credifyfbs app.html).
const INSTRUMENTS: { match: RegExp; inst: Instrument }[] = [
  {
    match: /\bPHQ[\s-]?9\b/i,
    inst: {
      name: 'PHQ-9',
      items: 9,
      bands: [
        { min: 0, max: 4, label: 'Minimal', severity: 'low' },
        { min: 5, max: 9, label: 'Mild', severity: 'mild' },
        { min: 10, max: 14, label: 'Moderate', severity: 'mod' },
        { min: 15, max: 19, label: 'Moderately severe', severity: 'high' },
        { min: 20, max: 27, label: 'Severe', severity: 'high' },
      ],
      alert: {
        itemIndex: 8,
        msg: 'PHQ-9 item 9 positive (thoughts of self-harm): assess suicide risk now.',
      },
    },
  },
  {
    match: /\bGAD[\s-]?7\b/i,
    inst: {
      name: 'GAD-7',
      items: 7,
      bands: [
        { min: 0, max: 4, label: 'Minimal', severity: 'low' },
        { min: 5, max: 9, label: 'Mild', severity: 'mild' },
        { min: 10, max: 14, label: 'Moderate', severity: 'mod' },
        { min: 15, max: 21, label: 'Severe', severity: 'high' },
      ],
    },
  },
  {
    match: /\bPCL[\s-]?5\b/i,
    inst: {
      name: 'PCL-5',
      items: 20,
      bands: [
        { min: 0, max: 30, label: 'Below provisional threshold', severity: 'low' },
        { min: 31, max: 80, label: 'Probable PTSD', severity: 'high' },
      ],
    },
  },
];

/** The form's scored items and the highest possible total. */
/**
 * ehr#363 (Jay 10-11): the Screeners tab lists screeners and assessments: the built-in
 * screeners, and any form with a score (a 0-n answer scale it adds up, like a PHQ-9).
 * credifyfbs#240: of the System Forms, the Form Builder's screeners (not its intake, consent
 * and clinical forms, which the Forms tab lists beside them).
 */
export function isScreener(f: {
  source?: string;
  category?: string;
  fields?: LibField[] | null;
  scoring?: RiskConfig | null;
}): boolean {
  if (f.source === 'system') return (f.category ?? 'screener') === 'screener';
  return (
    Boolean(f.scoring?.sections.length) || // ehr#429: a Form Builder form with its own scoring
    scoredItems(f.fields ?? []).items.length > 0
  );
}

/** ehr#429 - a scoring band's severity as the score history colours it. */
const severityOf = (s: string | undefined): Severity => {
  const v = (s ?? '').toLowerCase();
  if (v === 'high' || v === 'severe' || v === 'critical') return 'high';
  if (v === 'mod' || v === 'moderate' || v === 'medium') return 'mod';
  if (v === 'mild') return 'mild';
  return 'low';
};
const toBand = (b: Band): ScoreBand => ({
  min: Number(b.min),
  max: Number(b.max),
  label: (b.label ?? '').trim() || `${b.min}–${b.max}`,
  severity: severityOf(b.severity),
});

/**
 * ehr#429 - a form's score from its own scoring (the Form Builder's points, bands and alerts, or a
 * built-in screener's): the score, the most it can be, the band (of the first section with bands)
 * and the alerts. Null when nothing scored was answered.
 */
export function scoreFromScoring(
  cfg: RiskConfig,
  answers: Record<string, unknown>,
): { score: number; max: number; band: ScoreBand | null; alerts: string[] } | null {
  const secs = sectionScores(cfg, answers);
  if (!secs.some((s) => s.answered)) return null;
  const banded = secs.find((s) => s.answered && s.band);
  return {
    score: secs.reduce((n, s) => n + s.score, 0),
    max: secs.reduce((n, s) => n + s.max, 0),
    band: banded?.band ? toBand(banded.band) : null,
    alerts: secs.map((s) => s.message).filter(Boolean),
  };
}

export function scoredItems(fields: LibField[]): { items: ScoredItem[]; maxTotal: number } {
  const items: ScoredItem[] = [];
  let maxTotal = 0;
  for (const b of buildBlocks(fields)) {
    if (b.kind !== 'matrix' || b.options.length < 3) continue;
    for (const it of b.items) {
      items.push({ id: it.id, label: it.label, options: b.options });
      maxTotal += b.options.length - 1;
    }
  }
  return { items, maxTotal };
}

/** A known instrument, only when the name matches and the item count agrees. */
export function instrumentFor(formName: string, itemCount: number): Instrument | null {
  const hit = INSTRUMENTS.find((x) => x.match.test(formName) && x.inst.items === itemCount);
  return hit ? hit.inst : null;
}

const labelKey = (label: string) => 'label:' + label.trim().toLowerCase();

/**
 * One item's score from a stored answer: the option's position (a staff fill stores the
 * option text), or a portal scale answer already given as its number ("0"-"3"). Null when
 * unanswered or not one of the options.
 */
export function itemScore(item: ScoredItem, value: unknown): number | null {
  if (value == null || typeof value === 'boolean') return null;
  const s = (Array.isArray(value) ? String(value[0] ?? '') : String(value)).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n >= 0 && n < item.options.length ? n : null;
  }
  const exact = item.options.indexOf(s);
  if (exact >= 0) return exact;
  const i = item.options.findIndex((o) => o.trim().toLowerCase() === s.toLowerCase());
  return i >= 0 ? i : null;
}

/** A portal fill's answers (GET /submissions/:id/answers) keyed by field id and by label. */
export function portalAnswers(
  fields: { fieldId: string | null; label: string; value: unknown }[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.fieldId) out[f.fieldId] = f.value;
    if (f.label) out[labelKey(f.label)] = f.value;
  }
  return out;
}

/** ehr#592 - a question on the version of the form a fill was filled on. */
export interface FilledQuestion {
  id: string | null;
  label: string;
  type: string;
}

export interface ScoreFill {
  id: string;
  /** When it was filled (ISO). */
  at: string;
  /** Who filled it, as shown. */
  by: string;
  answers: Record<string, unknown>;
  /** ehr#592: a staff fill's saved questions (credifyfbs#241); none on an older fill. */
  layout?: LibField[] | null;
  /** ehr#592: a portal fill's questions as the client saw them; none when not known. */
  questions?: FilledQuestion[] | null;
  /** ehr#592: the score saved with the fill (ehr#499), on the form as it was then. */
  saved?: { total: number; band: string | null } | null;
}

/**
 * ehr#592 - where a fill's total and band come from: the score saved with it ('saved'), its own
 * saved questions for a form scored by its answer scales ('own'), or today's form ('current': an
 * older fill with neither, marked in the table).
 */
export type ScoreBasis = 'saved' | 'own' | 'current';
export interface ScoreHistory {
  items: ScoredItem[];
  maxTotal: number;
  instrument: Instrument | null;
  /** Oldest first. */
  fills: ScoreFill[];
  /** scores[item][fill] */
  scores: (number | null)[][];
  totals: (number | null)[];
  bands: (ScoreBand | null)[];
  alerts: string[];
  /** Where the severity changes (chart gridlines). */
  cutoffs: number[];
  /** ehr#592: per fill, where its total and band come from. */
  basis: ScoreBasis[];
  /** ehr#592: offVersion[item][fill], the question wasn't on the form that fill was filled on. */
  offVersion: boolean[][];
  /** ehr#592: answers to questions since removed from the form (not in the table). */
  hiddenAnswers: number;
}

/**
 * ehr#429 - the history from the form's own scoring: every scored question (radio, Yes / No,
 * checkboxes, rules, matrices) at its real points, as the Form Builder and the risk alerts count
 * them. AUDIT items 9-10 score 0 / 2 / 4, and DAST-10 and C-SSRS (Yes / No) get a score.
 */
function historyFromScoring(
  formName: string,
  fields: LibField[],
  fills: ScoreFill[],
  cfg: RiskConfig,
): ScoreHistory | null {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const ids = [...new Set(cfg.sections.flatMap((s) => s.fieldIds ?? []))].filter(
    (id) => cfg.fields[id],
  );
  if (!ids.length) return null;
  const items: ScoredItem[] = ids.map((id) => ({
    id,
    label: byId.get(id)?.label ?? id,
    options: cfg.fields[id]!.options,
  }));
  const sorted = [...fills].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  // A portal fill may key an answer by its label; give each question its answer under its id.
  const answersOf = (f: ScoreFill) => {
    const a: Record<string, unknown> = { ...f.answers };
    for (const it of items) if (a[it.id] === undefined) a[it.id] = f.answers[labelKey(it.label)];
    return a;
  };
  const per = sorted.map(answersOf);
  const scores = items.map((it) =>
    per.map((a) =>
      fieldAnswered(it.id, cfg.fields[it.id], a) ? fieldScore(it.id, cfg.fields[it.id], a) : null,
    ),
  );
  const results = per.map((a) => scoreFromScoring(cfg, a));
  const firstBands = cfg.sections.find((s) => (s.bands ?? []).length)?.bands ?? [];
  const bandsList = firstBands.map(toBand);
  const instrument: Instrument | null = bandsList.length
    ? {
        name: (cfg.sections[0]?.name ?? '').trim() || formName,
        items: items.length,
        bands: bandsList,
      }
    : null;
  return {
    items,
    maxTotal: results.find((r) => r)?.max ?? sectionScores(cfg, {}).reduce((n, s) => n + s.max, 0),
    instrument,
    fills: sorted,
    scores,
    totals: results.map((r) => (r ? r.score : null)),
    bands: results.map((r) => r?.band ?? null),
    alerts: results.map((r) => (r ? r.alerts.join(' ') : '')),
    cutoffs: bandsList.slice(1).map((b) => b.min),
    ...unversioned(sorted.length, items.length),
  };
}

const unversioned = (fills: number, items: number) => ({
  basis: Array.from({ length: fills }, (): ScoreBasis => 'current'),
  offVersion: Array.from({ length: items }, () => Array.from({ length: fills }, () => false)),
  hiddenAnswers: 0,
});

/** One fill's item scores and total on answer scales (a form with no scoring rules). */
function scaleTotals(
  items: ScoredItem[],
  fill: ScoreFill,
): { scores: (number | null)[]; total: number | null } {
  const scores = items.map((it) =>
    itemScore(it, fill.answers[it.id] ?? fill.answers[labelKey(it.label)]),
  );
  const hit = scores.filter((v): v is number => v != null);
  return { scores, total: hit.length ? hit.reduce((a, b) => a + b, 0) : null };
}

/** ehr#592 - a saved band (its label only): today's band of that name, else one named so. */
function savedBand(label: string | null, bands: ScoreBand[], total: number): ScoreBand | null {
  const l = (label ?? '').trim();
  if (!l) return null;
  const same = bands.find((b) => b.label.trim().toLowerCase() === l.toLowerCase());
  if (same) return same;
  const v = l.toLowerCase();
  const severity: Severity = /severe|high|critical|probable/.test(v)
    ? 'high'
    : /moderate|medium/.test(v)
      ? 'mod'
      : /mild/.test(v)
        ? 'mild'
        : 'low';
  return { min: total, max: total, label: l, severity };
}

// ehr#592: the kinds of question a score can count (not text, dates, signatures or headings).
const CHOICE = /radio|scale|select|dropdown|check|yes|toggle|rating|likert|matrix/i;
const hasAnswer = (v: unknown) =>
  v != null && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0);

/**
 * ehr#592 - each fill read on the version it was filled on. The columns stay today's questions:
 * a question that wasn't on a fill's version is blank for it (offVersion), and answers to
 * questions removed since are counted (hiddenAnswers). The total and band come from the score
 * saved with the fill when there is one (scored then, on its own version). A form scored only by
 * its answer scales is scored on the fill's own saved questions. Anything else is scored on
 * today's form, as before, and marked.
 */
function withVersions(
  h: ScoreHistory,
  formName: string,
  fields: LibField[],
  scoring?: RiskConfig | null,
): ScoreHistory {
  const todayIds = new Set(fields.map((f) => f.id));
  const todayLabels = new Set(fields.map((f) => labelKey(f.label)));
  const onToday = (q: FilledQuestion) =>
    (!!q.id && todayIds.has(q.id)) || todayLabels.has(labelKey(q.label));
  // A saved score is the first scored section's (screener-score.ts), so it is the total only on a
  // form with one section.
  const oneSection = !scoring?.sections.length || sectionScores(scoring, {}).length <= 1;
  const bandList = h.instrument?.bands ?? [];
  const out: ScoreHistory = {
    ...h,
    scores: h.scores.map((r) => [...r]),
    totals: [...h.totals],
    bands: [...h.bands],
    ...unversioned(h.fills.length, h.items.length),
  };
  h.fills.forEach((f, j) => {
    const asked: FilledQuestion[] | null = f.questions?.length
      ? f.questions
      : f.layout?.length
        ? f.layout.map((q) => ({ id: q.id, label: q.label, type: q.type }))
        : null;
    if (asked) {
      h.items.forEach((it, i) => {
        const on = asked.some(
          (q) => (!!q.id && q.id === it.id) || labelKey(q.label) === labelKey(it.label),
        );
        if (!on) {
          out.offVersion[i]![j] = true;
          out.scores[i]![j] = null;
        }
      });
      for (const q of asked) {
        if (onToday(q) || !CHOICE.test(q.type)) continue;
        const v = (q.id ? f.answers[q.id] : undefined) ?? f.answers[labelKey(q.label)];
        if (hasAnswer(v)) out.hiddenAnswers++;
      }
    }
    if (f.saved && Number.isFinite(f.saved.total) && oneSection) {
      out.totals[j] = f.saved.total;
      out.bands[j] = savedBand(f.saved.band, bandList, f.saved.total);
      out.basis[j] = 'saved';
    } else if (!scoring?.sections.length && f.layout?.length) {
      const own = scoredItems(f.layout).items;
      const { total } = scaleTotals(own, f);
      const inst = instrumentFor(formName, own.length);
      out.totals[j] = total;
      out.bands[j] =
        total == null || !inst
          ? null
          : (inst.bands.find((b) => total >= b.min && total <= b.max) ?? null);
      out.basis[j] = 'own';
    }
  });
  return out;
}

/**
 * ehr#614 (Chase) - what a dot on the score chart says on hover: the score and the date it was
 * filled, nothing else (not the fill number, "Total" or the band).
 */
export function pointTip(total: number, at: string): string {
  const d = new Date(at);
  const date = Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return date ? `Score ${total} · ${date}` : `Score ${total}`;
}

/** ehr#614 - one severity band shaded behind the chart, from `from` up to `to`. */
export interface ChartBand {
  from: number;
  to: number;
  label: string;
  severity: Severity;
}

/**
 * ehr#614 (Chase: "Shouldn't the PCL be showing the severity bands?") - the bands the chart
 * shades, lowest first. A band reaches up to where the next one starts, so the shading has no
 * gaps (as the progress note's Measures graph draws them), and stops at the top of the chart.
 */
export function chartBands(bands: ScoreBand[] | undefined, yMax: number): ChartBand[] {
  const sorted = [...(bands ?? [])]
    .filter((b) => Number.isFinite(b.min))
    .sort((a, b) => a.min - b.min);
  const out: ChartBand[] = [];
  sorted.forEach((b, i) => {
    const from = Math.max(0, b.min);
    if (from >= yMax) return;
    const next = sorted[i + 1];
    const to = Math.min(next ? next.min : yMax, yMax);
    if (to > from) out.push({ from, to, label: b.label, severity: b.severity });
  });
  return out;
}

export function buildScoreHistory(
  formName: string,
  fields: LibField[],
  fills: ScoreFill[],
  scoring?: RiskConfig | null,
): ScoreHistory | null {
  const h = scoring?.sections.length
    ? historyFromScoring(formName, fields, fills, scoring)
    : historyFromScales(formName, fields, fills);
  return h && withVersions(h, formName, fields, scoring);
}

function historyFromScales(
  formName: string,
  fields: LibField[],
  fills: ScoreFill[],
): ScoreHistory | null {
  const { items, maxTotal } = scoredItems(fields);
  if (!items.length) return null;
  const instrument = instrumentFor(formName, items.length);
  const sorted = [...fills].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const per = sorted.map((f) => scaleTotals(items, f));
  const scores = items.map((_, i) => per.map((p) => p.scores[i] ?? null));
  const totals = per.map((p) => p.total);
  const bands = totals.map((t) =>
    t == null || !instrument
      ? null
      : (instrument.bands.find((b) => t >= b.min && t <= b.max) ?? null),
  );
  const alerts = sorted.map((_, j) => {
    const a = instrument?.alert;
    const v = a ? scores[a.itemIndex]?.[j] : null;
    return a && v != null && v > 0 ? a.msg : '';
  });
  const cutoffs = instrument ? instrument.bands.slice(1).map((b) => b.min) : [];
  return {
    items,
    maxTotal,
    instrument,
    fills: sorted,
    scores,
    totals,
    bands,
    alerts,
    cutoffs,
    ...unversioned(sorted.length, items.length),
  };
}
